import {DataTable, DataTableColumn, DataTableSortStatus} from "mantine-datatable";
import {useContext, useEffect, useState} from "react";
import {ActionIcon, Button, Group, NumberInput, Pagination, Stack, Text, TextInput, Tooltip} from "@mantine/core";
import {IconEdit, IconEye} from "@tabler/icons-react";
import ComputerPageContext from "../../../../../contexts/pages/computer/ComputerPageContext";
import Computer from "../../../../../api/computer/Computer";
import ListQuery from "../../../../../api/computer/ListQuery";
import {useNavigate} from "react-router-dom";
import DateTime from "../../../../components/_utils/date-time/DateTime";

type ComputerMantineTableProps = {}

const initSortStatus: DataTableSortStatus = {
    columnAccessor: 'id',
    direction: 'desc',
}

const isFilterActive = (value: string | number | undefined | null) =>
    value !== undefined && value !== null && value !== '';

/**
 * Simple implementation of computers table in Mantine DataTable.
 * It's so simple, there's no action buttons etc.
 * Renders 'Brak danych', if given page is undefined.
 * @param _props
 * @constructor
 */
function ComputerMantineTable(_props: ComputerMantineTableProps) {
    const navigate = useNavigate();
    const pageContext = useContext(ComputerPageContext);
    const [page, _2] = pageContext.page;
    const [_1, setComputerToEdit] = pageContext.computerToEdit;
    const [query, setQuery] = pageContext.query;

    const [sortStatus, setSortStatus] = useState<DataTableSortStatus>(initSortStatus);

    const updateQuery = (patch: Partial<ListQuery>) => {
        setQuery({...query, ...patch});
    }

    const columns: DataTableColumn[] = [
        {accessor: 'id', title: '#', sortable: true},

        {
            accessor: 'title',
            title: 'Nazwa komputera',
            sortable: true,
            filter: _ => (
                <Stack>
                    <TextInput
                        placeholder="Nazwa komputera"
                        value={query.q ?? ''}
                        onChange={ev => updateQuery({q: ev.target.value || undefined})}
                    />

                    <Button
                        color="red"
                        size="xs"
                        children="Wyczyść"
                        onClick={() => updateQuery({q: undefined})}
                    />
                </Stack>
            ),
            filtering: isFilterActive(query.q),
            render: (r: any) => <Text c={r.online ? 'green' : 'red'} children={r.title}/>
        },
        {
            accessor: 'description',
            title: 'Opis',
            filter: _ => (
                <Stack>
                    <TextInput
                        placeholder="Opis"
                        value={query.description ?? ''}
                        onChange={ev => updateQuery({description: ev.target.value || undefined})}
                    />

                    <Button
                        color="red"
                        size="xs"
                        children="Wyczyść"
                        onClick={() => updateQuery({description: undefined})}
                    />
                </Stack>
            ),
            filtering: isFilterActive(query.description),
        },
        {
            accessor: 'userName',
            title: 'Nazwa użytkownika',
            sortable: true,
            sortKey: 'user_name',
            filter: _ => (
                <Stack>
                    <TextInput
                        placeholder="Nazwa użytkownika"
                        value={query.userName ?? ''}
                        onChange={ev => updateQuery({userName: ev.target.value || undefined})}
                    />

                    <Button
                        color="red"
                        size="xs"
                        children="Wyczyść"
                        onClick={() => updateQuery({userName: undefined})}
                    />
                </Stack>
            ),
            filtering: isFilterActive(query.userName),
        },
        {
            accessor: 'domainName',
            title: 'Domena',
            sortable: true,
            sortKey: 'domain_name',
            filter: _ => (
                <Stack>
                    <TextInput
                        placeholder="Domena"
                        value={query.domainName ?? ''}
                        onChange={ev => updateQuery({domainName: ev.target.value || undefined})}
                    />

                    <Button
                        color="red"
                        size="xs"
                        children="Wyczyść"
                        onClick={() => updateQuery({domainName: undefined})}
                    />
                </Stack>
            ),
            filtering: isFilterActive(query.domainName),
        },
        {
            accessor: 'config.maxIdleSeconds',
            title: 'Czas bezczynności',
            sortable: true,
            sortKey: 'config__max_idle_seconds',
            filter: _ => (
                <Stack>
                    <NumberInput
                        placeholder="Sekundy"
                        value={query.maxIdleSeconds}
                        onChange={(value) => updateQuery({
                            maxIdleSeconds: typeof value === 'number' ? value : undefined
                        })}
                        allowDecimal={false}
                        allowNegative={false}
                    />

                    <Button
                        color="red"
                        size="xs"
                        children="Wyczyść"
                        onClick={() => updateQuery({maxIdleSeconds: undefined})}
                    />
                </Stack>
            ),
            filtering: isFilterActive(query.maxIdleSeconds),
            render: (r) => (
                <Tooltip
                    color="dark"
                    openDelay={3000}
                    label="Liczba minut po których nieużywane programy są wyłączane. '-' oznacza wartość domyślną."
                    children={<div>{r.config ? (r.config as any).maxIdleSeconds / 60 + ' minut' : '-'}</div>}
                />
            )
        },
        {
            accessor: 'lastConnected',
            title: 'Ostatnie połączenie',
            sortable: true,
            sortKey: 'last_connected',
            render: (r: any) => r.lastConnected ? <DateTime dateTime={r.lastConnected}/> : "-"
        },
        {
            accessor: 'lastDisconnected',
            title: 'Ostatnio widziano',
            sortable: true,
            sortKey: 'last_disconnected',
            render: (r: any) => r.lastDisconnected ? <DateTime dateTime={r.lastDisconnected}/> : '-'
        },
        {
            accessor: 'config.programs',
            title: 'Programy',
            sortable: false,
            noWrap: true,
            filter: _ => (
                <Stack>
                    <TextInput
                        placeholder="Program"
                        value={query.programTitle ?? ''}
                        onChange={ev => updateQuery({programTitle: ev.target.value || undefined})}
                    />

                    <Button
                        color="red"
                        size="xs"
                        children="Wyczyść"
                        onClick={() => updateQuery({programTitle: undefined})}
                    />
                </Stack>
            ),
            filtering: isFilterActive(query.programTitle),
            render: (r) => (
                <Group justify="left" wrap="nowrap">
                    {(r as any)?.config?.programs?.map(({title}: any) => title).join(", ")}
                </Group>
            )
        },
        {
            accessor: 'actions',
            title: '',
            sortable: false,
            noWrap: true,
            render: (r) => (
                <Group justify="right" gap="md" wrap="nowrap">
                    <ActionIcon
                        size="sm"
                        variant="subtle"
                        color="green"
                        onClick={() => navigate(`/computer/${r.id}`)}
                        children={<IconEye size={16}/>}
                    />

                    <ActionIcon
                        size="sm"
                        variant="subtle"
                        color="blue"
                        onClick={() => {
                            setComputerToEdit(r as any as Computer)
                        }}
                        children={<IconEdit size={16}/>}
                    />
                </Group>
            )
        }
    ];

    const onSortStatusChanged = () => {
        const newQuery = {
            ...query,
            sortBy: sortStatus.sortKey ?? sortStatus.columnAccessor,
            sortDir: sortStatus.direction
        };
        setQuery(newQuery);
    }

    useEffect(onSortStatusChanged, [sortStatus]);

    return (
        <Stack>
            <DataTable
                styles={{header: {background: 'var(--mantine-color-dark-7)'}}}
                backgroundColor="transparent"
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                highlightOnHover
                withColumnBorders
                columns={columns}
                records={page?.results ?? []}
                minHeight="50vh"
                noRecordsText="Ale pusto..."
                fetching={pageContext.isFetching}
            />

            <Pagination
                total={page?.totalPages ?? 1}
                onChange={(e) => setQuery({...query, page: e})}
            />
        </Stack>
    );
}

export default ComputerMantineTable;

// TODO: ComputerMantineTable.tsx
//  - Improve table [if we want to use it]
